import { SOLO_LEADER_TRIAL_PROCESSED_MEETING_LIMIT } from "./soloLeaderBilling.ts";

export type SoloModeAccountMode = "solo_trial" | "coach_led" | null;
export type SoloTrialStateKind =
  | "coach_led"
  | "solo_trial_active"
  | "solo_trial_expired"
  | "legacy_or_unknown";

export interface ResolveSoloModeTrialStateInput {
  accountMode?: string | null;
  trialStartedAt?: string | null;
  trialEndsAt?: string | null;
  hasCoachConnection: boolean;
  now?: Date;
}

export interface SoloModeTrialState {
  kind: SoloTrialStateKind;
  isSoloTrialActive: boolean;
  isSoloTrialExpired: boolean;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  daysRemaining: number | null;
}

export const SOLO_TRIAL_LIMITS = {
  transcriptUploads: null,
  processedMeetings: SOLO_LEADER_TRIAL_PROCESSED_MEETING_LIMIT,
} as const;

export function resolveSoloModeTrialState(input: ResolveSoloModeTrialStateInput): SoloModeTrialState {
  const trialStartedAt = input.trialStartedAt ?? null;
  const trialEndsAt = input.trialEndsAt ?? null;

  if (input.hasCoachConnection || input.accountMode === "coach_led") {
    return buildTrialState({
      kind: "coach_led",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: null,
    });
  }

  if (input.accountMode !== "solo_trial" || !trialStartedAt || !trialEndsAt) {
    return buildTrialState({
      kind: "legacy_or_unknown",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: null,
    });
  }

  const now = input.now ?? new Date();
  const endsAt = new Date(trialEndsAt);
  if (Number.isNaN(endsAt.getTime())) {
    return buildTrialState({
      kind: "legacy_or_unknown",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: null,
    });
  }

  if (endsAt.getTime() <= now.getTime()) {
    return buildTrialState({
      kind: "solo_trial_expired",
      trialStartedAt,
      trialEndsAt,
      daysRemaining: 0,
    });
  }

  return buildTrialState({
    kind: "solo_trial_active",
    trialStartedAt,
    trialEndsAt,
    daysRemaining: Math.max(1, Math.ceil((endsAt.getTime() - now.getTime()) / 86_400_000)),
  });
}

export function shouldAllowSoloTrialUsage(params: {
  stateKind: SoloTrialStateKind;
  used: number;
  limit: number | null;
}): boolean {
  return params.stateKind !== "solo_trial_expired";
}

function buildTrialState(params: {
  kind: SoloTrialStateKind;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  daysRemaining: number | null;
}): SoloModeTrialState {
  return {
    kind: params.kind,
    isSoloTrialActive: params.kind === "solo_trial_active",
    isSoloTrialExpired: params.kind === "solo_trial_expired",
    trialStartedAt: params.trialStartedAt,
    trialEndsAt: params.trialEndsAt,
    daysRemaining: params.daysRemaining,
  };
}
